import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const StudentProfile = () => {
  const { user, studio, refreshUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [enrollments, setEnrollments] = useState([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(true);
  const [form, setForm] = useState({
    name: user?.name || '',
    phone: user?.phone || ''
  });
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    const fetchEnrollments = async () => {
      try {
        const { data } = await api.get('/enroll/my');
        setEnrollments(data.enrollments || []);
      } catch (err) {
        console.error('Failed to load enrollments:', err.message);
      } finally {
        setLoadingEnrollments(false);
      }
    };
    fetchEnrollments();
  }, []);

  // Reset form when user changes
  useEffect(() => {
    if (user) {
      setForm({ name: user.name || '', phone: user.phone || '' });
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.put('/auth/profile', form);
      await refreshUser();
      setEditing(false);
      setSuccess('Profile updated');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    setResetLoading(true);
    try {
      await api.post('/auth/reset-password', {
        email: user?.email
      });
      setResetSent(true);
    } catch (err) {
      setError('Failed to send password reset email');
    } finally {
      setResetLoading(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setForm({ name: user?.name || '', phone: user?.phone || '' });
    setError('');
  };

  const activeEnrollments = enrollments.filter(e => e.status === 'active');
  const studioNames = [...new Set(activeEnrollments.map(e => e.studio?.name).filter(Boolean))];

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">My Profile</h1>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 max-w-lg">
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            {editing ? (
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              />
            ) : (
              <p className="text-gray-900">{user?.name || '-'}</p>
            )}
          </div>

          {/* Email (read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <p className="text-gray-900">{user?.email}</p>
            <p className="text-xs text-gray-400 mt-1">Managed by Auth0 — cannot be changed here</p>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            {editing ? (
              <input
                type="tel"
                value={form.phone}
                onChange={e => setForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="Enter your phone number"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              />
            ) : (
              <p className="text-gray-900">{user?.phone || '-'}</p>
            )}
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <p className="text-gray-900 capitalize">{user?.role || '-'}</p>
          </div>

          {/* Studio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Studio</label>
            {loadingEnrollments ? (
              <p className="text-gray-400 text-sm">Loading...</p>
            ) : studioNames.length > 0 ? (
              <p className="text-gray-900">{studioNames.join(', ')}</p>
            ) : studio?.name ? (
              <p className="text-gray-900">{studio.name}</p>
            ) : (
              <p className="text-gray-500">Not enrolled in any studio</p>
            )}
          </div>

          {/* Active courses */}
          {activeEnrollments.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Active Courses ({activeEnrollments.length})
              </label>
              <ul className="space-y-1">
                {activeEnrollments.map(e => (
                  <li key={e._id} className="text-gray-900 text-sm flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block"></span>
                    {e.course?.name || 'Unknown Course'}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-6 flex gap-3 mb-6">
          {editing ? (
            <>
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-brand-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancel}
                className="text-gray-600 px-5 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={() => setEditing(true)}
              className="bg-brand-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors"
            >
              Edit Profile
            </button>
          )}
        </div>
      </div>

      {/* Reset Password */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 max-w-lg mt-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Password</h2>
        <p className="text-sm text-gray-500 mb-4">
          Reset your password via email.
        </p>
        {resetSent ? (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-700">Password reset email sent to {user?.email}. Check your inbox.</p>
          </div>
        ) : (
          <button
            onClick={handleResetPassword}
            disabled={resetLoading}
            className="border border-brand-500 text-brand-500 px-5 py-2 rounded-lg text-sm font-medium hover:bg-brand-50 disabled:border-gray-300 disabled:text-gray-400 transition-colors"
          >
            {resetLoading ? 'Sending...' : 'Send Password Reset Email'}
          </button>
        )}
      </div>

      {/* Google Classroom Integration */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 max-w-lg mt-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Google Classroom</h2>
        <p className="text-sm text-gray-500 mb-4">
          Access your assignments, submit homework, and view grades.
        </p>

        {activeEnrollments.some(e => e.course?.googleClassroom?.courseUrl) ? (
          <div className="space-y-3">
            {activeEnrollments
              .filter(e => e.course?.googleClassroom?.courseUrl)
              .map(e => (
                <a
                  key={e._id}
                  href={e.course.googleClassroom.courseUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-brand-50 hover:border-brand-200 border border-gray-100 transition-colors"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">{e.course.name}</p>
                    <p className="text-xs text-gray-500">Open in Google Classroom</p>
                  </div>
                  <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                  </svg>
                </a>
              ))}
          </div>
        ) : (
          <div className="text-center py-4">
            <div className="w-12 h-12 rounded-full bg-yellow-50 flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342" />
              </svg>
            </div>
            <p className="text-sm text-gray-500 mb-3">Your teacher hasn't connected Google Classroom yet, or you haven't been invited.</p>
            <a
              href="https://classroom.google.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-brand-500 hover:text-brand-600 font-medium"
            >
              Open Google Classroom →
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentProfile;
